import type { ComputedRef } from 'vue';

import { useFeatureFlags } from '~/composables/useFeatureFlags';
import { UNIFIED_ROUTE_EDUCATORS } from '~/constants/routes/unified';
import { ClusterProduct } from '~/graphql/generated/graphql';
import { useClusterRegistryStore } from '~/stores/clusterRegistry';
import { useConfigStore } from '~/stores/config';
import { buildClusterUrl } from '~/utils/clusters';

/**
 * The schools site for this region. Once the schools hosts are live it is the landing page of
 * this cluster's schools sibling. While they are still dark there is no landing to point at, so
 * it stays on this cluster, where /educators forwards to the demo request.
 *
 * Everything that needs the Nuxt context is read before the flag's await, so a caller can start
 * this before its own first await and await the answer later.
 */
export const useSchoolsSiteUrl = async (): Promise<ComputedRef<string>> => {
  const configStore = useConfigStore();
  const clusterRegistry = useClusterRegistryStore();
  const scheme = useRuntimeConfig().public.useHttp ? 'http' : 'https';
  const schoolsClustersEnabled = await useFeatureFlags().getFlag('schools-clusters-enabled');

  return computed(() => {
    const sibling = clusterRegistry.clusters.find(cluster =>
      cluster.id === configStore.schoolDataClusterId && cluster.product === ClusterProduct.Schools,
    );
    return schoolsClustersEnabled && sibling
      ? buildClusterUrl(sibling.host, sibling.pathPrefix, { scheme })
      : UNIFIED_ROUTE_EDUCATORS;
  });
};
